import { visibleTiles, worldToScreenX, worldToScreenY } from './projection';
import type { Camera, Viewport } from './projection';
import { SIDES, VARIANTS, blendPriority } from './terrainAtlas';
import type { TerrainAtlas } from './terrainAtlas';
import { TILE } from '../sim/constants';

/*
 * ציור הקרקע.
 *
 * מצייר רק את האריחים הנראים. כל אריח הוא `drawImage` אחד מ-atlas שנבנה מראש —
 * **אותה עלות בדיוק כמו fillRect בצבע שטוח**, אבל עם מרקם.
 *
 * מעל זה, אריח שגובל בסוג קרקע "חזק" יותר מקבל שכבת מעבר בצד הרלוונטי, וכך הגבול
 * מיטשטש במקום להיות קו ישר של פיקסלים. רק אריחי גבול משלמים על זה; אריח בתוך
 * שדה אחיד נשאר ציור אחד.
 *
 * שקלתי מטמון של המפה כולה לקנבס אחד וּויתרתי: 96x96 אריחים ב-32px הם ~37MB,
 * מחיר שלא משלמים בטלפון. אם פרופיל יראה צורך, המקום להוסיף מטמון הוא כאן בלבד.
 */

export interface TerrainLayers {
    readonly w: number;
    readonly h: number;
    readonly terrain: Uint8Array;
    /** שכבה דינמית — במשחק חי מגיעה ממצב המשחק, לא מהמפה. */
    readonly ore: Uint16Array;
}

/** כמות העפרות שמעליה האריח מצויר בדרגת העושר הגבוהה ביותר. */
const ORE_RICH = 600;

/**
 * גיוון דטרמיניסטי לפי מיקום האריח — אותו אריח מקבל תמיד את אותה וריאציה, כך
 * שהמפה לא "מרצדת" בזמן גלילה. אין כאן RNG: הגיוון הוא תצוגה בלבד.
 *
 * הגיבוב חייב ערבול אמיתי. הניסיון הראשון היה `(x*A ^ y*B) >>> 13` — הוא נראה
 * אקראי אבל ייצר רצפים של 2-3 אריחים זהים, והמפה נראתה מפוספסת.
 * `shade.test.ts` מודד את זה במספרים במקום להעריך בעין.
 */
export function shadeIndex(x: number, y: number): number {
    let h = (Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
    // ה->>> 0 האחרון אינו קישוט: ^ ב-JS ממיר ל-int32, ובלעדיו התוצאה שלילית.
    h = (h ^ (h >>> 12)) >>> 0;
    return h % SHADE_LEVELS;
}

export const SHADE_LEVELS = VARIANTS;

/** צדדים בסדר: 0 מלמעלה, 1 מימין, 2 מלמטה, 3 משמאל. */
const NEIGHBOURS: readonly (readonly [number, number])[] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
];

export function drawTerrain(
    ctx: CanvasRenderingContext2D,
    layers: TerrainLayers,
    cam: Camera,
    vp: Viewport,
    atlas: TerrainAtlas,
): void {
    const bounds = visibleTiles(cam, vp, layers.w, layers.h);
    const step = cam.zoom;
    const cell = atlas.cell;

    for (let ty = bounds.minY; ty <= bounds.maxY; ty++) {
        // מעגלים כלפי מטה ומחשבים את הקצה הבא — כך אין תפרים של תת-פיקסל בין אריחים.
        const top = Math.floor(worldToScreenY(ty * TILE, cam, vp));
        const bottom = Math.floor(worldToScreenY(ty * TILE, cam, vp) + step);
        const height = bottom - top;
        const rowIndex = ty * layers.w;

        for (let tx = bounds.minX; tx <= bounds.maxX; tx++) {
            const left = Math.floor(worldToScreenX(tx * TILE, cam, vp));
            const right = Math.floor(worldToScreenX(tx * TILE, cam, vp) + step);
            const width = right - left;

            const index = rowIndex + tx;
            const terrainId = layers.terrain[index] ?? 0;
            const variant = shadeIndex(tx, ty);

            ctx.drawImage(
                atlas.canvas,
                atlas.baseX(terrainId, variant),
                atlas.baseY(),
                cell,
                cell,
                left,
                top,
                width,
                height,
            );

            // ---- מעברים: שכן "חזק" יותר גולש פנימה
            const priority = blendPriority(terrainId);
            for (let side = 0; side < SIDES; side++) {
                const offset = NEIGHBOURS[side];
                if (!offset) continue;
                const nx = tx + offset[0];
                const ny = ty + offset[1];
                if (nx < 0 || ny < 0 || nx >= layers.w || ny >= layers.h) continue;

                const neighbourId = layers.terrain[ny * layers.w + nx] ?? 0;
                if (neighbourId === terrainId) continue;
                if (blendPriority(neighbourId) <= priority) continue;

                ctx.drawImage(
                    atlas.canvas,
                    atlas.edgeX(neighbourId, side),
                    atlas.edgeY(),
                    cell,
                    cell,
                    left,
                    top,
                    width,
                    height,
                );
            }

            // ---- עפרות מעל הכול
            const amount = layers.ore[index] ?? 0;
            if (amount > 0) {
                const level = amount >= ORE_RICH ? 2 : amount >= ORE_RICH / 3 ? 1 : 0;
                ctx.drawImage(
                    atlas.canvas,
                    atlas.oreX(level),
                    atlas.oreY(),
                    cell,
                    cell,
                    left,
                    top,
                    width,
                    height,
                );
            }
        }
    }
}

/** קו גבול דק סביב המפה, כדי שהקצה ייקרא כקצה ולא כאזור שלא נטען. */
export function drawMapBorder(
    ctx: CanvasRenderingContext2D,
    layers: TerrainLayers,
    cam: Camera,
    vp: Viewport,
    color: string,
): void {
    const left = worldToScreenX(0, cam, vp);
    const top = worldToScreenY(0, cam, vp);
    const right = worldToScreenX(layers.w * TILE, cam, vp);
    const bottom = worldToScreenY(layers.h * TILE, cam, vp);

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(left, top, right - left, bottom - top);
}
